import type { IssueDefinition } from "../../issue-definition";
import { ISSUE_CATEGORY } from "../../issue-enums";
import { ISSUE_OPERATOR_ACTION } from "../../issue-operator-action";

/**
 * The loop cannot SEE this cluster — a different fact from the one below, and
 * the quieter of the two.
 *
 * `VIBE_TENANT_CLUSTER_OUT_OF_BAND_DRIFT` reports an answer we did not like:
 * the drift check ran, returned a verdict, and the verdict named a change
 * nobody made through the provisioner. This code reports NO ANSWER AT ALL —
 * the preview threw, or died, or a precondition above it never resolved — and
 * that is worse, because a HEALTHY cluster is only ever observed. No verdict
 * means no drift, no drift means no converge, and the cluster reads HEALTHY on
 * every dashboard while nothing about it is actually known.
 *
 * 🔴 IT EXISTS BECAUSE THE COUNTER ALREADY EXISTED AND NOTHING WAS ALLOWED TO
 * ACT ON IT. The whole prod fleet was unobservable from 2026-07-22 12:10 UTC
 * to 2026-07-23 11:38 UTC — 86 ticks reporting `failed: 4` out of 4 clusters
 * scanned — with every cluster HEALTHY, every `statusReason` null, no failed
 * cron run, and not one row in this table. The count was in the cron
 * execution log the entire time; nobody was looking, and nothing looked on
 * anybody's behalf.
 *
 * THE ALARM IS THE WHOLE RESPONSE. A preview that returns no verdict means
 * nobody can see what an apply would do, so the loop never converges a cluster
 * on the strength of one — it tells a human, who chooses. Converging anyway is
 * how a single invalid WAF rule, which failed every tenant's ingress preview,
 * became an unattended `pulumi up` of every other stack in business hours.
 *
 * It is raised only once the consecutive count reaches the alarm threshold,
 * so the debounce lives in the detector and this row opens — and pages — once
 * per episode. A cluster that recovers clears its count and its row; breaking
 * again reopens it.
 *
 * `rawPayload` carries the consecutive count and the last fault message,
 * because the ONE thing that could not be recovered about the two-day window
 * was why it started. The tick recorded counts and no reasons, and pod logs
 * do not survive the week.
 */
export const VIBE_TENANT_CLUSTER_UNOBSERVABLE = {
  category: ISSUE_CATEGORY.OBSERVABILITY,
  message:
    "The reconcile loop cannot observe this Vibe tenant cluster. Its drift check has failed to return a verdict for several consecutive ticks — the pulumi preview threw, was killed, or a step before it never resolved — so nothing is currently known about the cluster's infrastructure. This is not a report that something drifted; it is a report that nobody can tell. The loop applies nothing to a cluster it cannot preview, so the cluster keeps its status and nothing changes until someone acts. The last fault message and the consecutive count are on the issue payload. Cleared automatically as soon as one drift check returns any verdict at all.",
  severity: "critical",
  // Pages: a blind cluster reads HEALTHY on every dashboard, which is how the
  // whole prod fleet went unobserved for a day with nothing saying so.
  page: true,
  remedy:
    "Read the last fault message on the issue payload first — it names why the preview could not return a verdict. A transient external fault (Pulumi backend outage, throttling, a killed runner) clears on its own at the next tick. A fault in the program or the provider's input validation (for example `Invalid or unknown key`) is a code defect: fix it and deploy, and do NOT force a converge, because a converge applies every other stack before failing on the same one. Force a converge only when the fault is one an apply itself repairs — typically a stack whose preview needs an output a sibling stack has never exported — and only once you accept that it applies every change accumulated since the last converge, node pools included, with no preview first.",
  operatorAction: ISSUE_OPERATOR_ACTION.VIBE_FORCE_CONVERGE_CLUSTER
} as const satisfies IssueDefinition;
